import { Badge, hexToRGB, mq, Spinner } from '@kvdbil/components';
import React, { ReactNode, useCallback, useEffect } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useHistory, withRouter } from 'react-router';
import { renderRoutes } from '~/config/routes/renderRoutes';
import { createStructuredSelector } from 'reselect';
import styled from 'styled-components';
import Content from '~/App/shared/components/Layout/Content';
import Head from '~/App/shared/components/Head';
import Link from '~/App/shared/components/Link';
import { ReduxStore } from '~/App/shared/interfaces/store';
import {
  AuctionNotification,
  FavouriteNotification,
  SalesAgreementNotification,
  SavedSearchNotification
} from '~/App/shared/interfaces/store/Notifications';
import { translateRoute } from '~/App/shared/localization';
import {
  auctionNotifications,
  favouriteNotifications,
  salesAgreementsNotifications,
  savedSearchesNotifications
} from '~/App/shared/selectors/notificationsSelector';
import { session as sessionSelector } from '~/App/shared/selectors/sessionSelector';
import { targetedAuctionsFeatureEnabled } from '~/config/public/environment';
import { countSavedSearchNotifications } from '~/helpers/notifications';
import { isActiveUrl } from '~/helpers/router';
import {
  getTargetedAuctionGroupNameById,
  getTargetedAuctionGroupUrlById
} from '~/helpers/targetedAuctions';
import { useTranslation } from '~/Locale';
import { useFeatureFlagValue } from '~/App/components/FeatureFlags';
import ComponentPadding from '~/App/shared/components/Layout/ComponentPadding';
import { auctionListFacilities } from '~/App/shared/selectors/auctionListSelector';
import { getFacilities } from '~/App/shared/actions/facilities';
import { RouteComponentProps } from '~/App/shared/interfaces/routes';
import Section from '~/App/shared/components/Layout/Section';
import { SignIn } from '~/App/shared/components/Auth/SignIn/components/SignIn';
import useLocalization from '~/App/shared/hooks/useLocalization';

const SubMenuContainer = styled.div`
  background: ${({ theme }) => theme.colors.grayscaleToned.dark2};
  text-align: center;
  display: none;

  ${mq('tablet')} {
    display: block;
  }
`;

const SubMenuNav = styled.ul`
  max-width: 1024px;
  margin-left: auto;
  margin-right: auto;
  padding-left: 0.5rem;
  padding-right: 0.5rem;
  width: 100%;
  clear: both;
  display: flex;
  justify-content: space-between;

  ${mq('mobileS')} {
    padding-left: 0.75rem;
    padding-right: 0.75rem;
  }

  ${mq('tablet')} {
    padding-left: 1rem;
    padding-right: 1rem;
  }
`;

const SubMenuNavItem = styled.li<{ isActive: boolean }>`
  border-bottom: 3px solid transparent;
  color: ${({ theme }) => hexToRGB(theme.colors.common.white, 0.75)};
  transition:
    color 0.3s ease,
    border 0.3s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.common.white};
    border-bottom-color: ${({ theme }) => theme.colors.common.white};
  }

  ${({ theme, isActive }) =>
    isActive &&
    `
    color: ${theme.colors.common.white};
    border-bottom-color: ${theme.colors.common.white};
  `}
`;

const StyledLink = styled(Link)`
  display: block;
  font-family: ${({ theme }) => theme.typography.fontAlternativeFamily};
  font-size: 12px;
  padding: 0.75em 0.5em;
  color: inherit;
  margin-bottom: -3px;
`;

const StyledBadge = styled(Badge)`
  margin-left: 0.5rem;
`;

const SpinnerContainer = styled.div`
  margin: auto;
  padding: 12vh 0 30vh;
`;

interface Props extends Partial<StateProps>, RouteComponentProps {
  children: ReactNode;
}

const Account = ({ location, children, ...props }: Props) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation();

  const isPartnershipOnline = useFeatureFlagValue('partnership-page');
  const { domainLocale } = useLocalization();
  const isSwedishDomain = domainLocale === 'sv';

  const session = useSelector(sessionSelector);
  const {
    auctionNotifications = [],
    favouriteNotifications = [],
    savedSearchesNotifications = [],
    salesAgreementsNotifications = []
  } = props;

  const handlePressCreateAccount = useCallback(() => {
    history.push(
      translateRoute(t, '/ROUTES.MEMBER/ROUTES.MEMBER_REGISTER_PRIVATE')
    );
  }, [history, t]);

  const handlePressForgotPassword = useCallback(
    (email?: string) => {
      if (email) sessionStorage.setItem('email', email);
      const url = translateRoute(
        t,
        '/ROUTES.MEMBER/ROUTES.MEMBER_FORGOT_PASSWORD'
      );

      history.push(url);
    },
    [history, t]
  );

  // TODO: [KVDBIL-1634] this should be using a global store when its moved from auction list...
  const facilities = useSelector(auctionListFacilities);
  useEffect(() => {
    if (!facilities.length) {
      dispatch(getFacilities());
    }
  }, [dispatch, facilities]);

  const savedSearchesNotificationsAmount = countSavedSearchNotifications(
    savedSearchesNotifications
  );

  const items = [
    {
      url: translateRoute(
        t,
        '/ROUTES.MY_ACCOUNT/ROUTES.MY_ACCOUNT_SAVED_OBJECTS'
      ),
      text: t('Saved objects'),
      notificationAmount: favouriteNotifications.length
    },
    {
      url: translateRoute(
        t,
        '/ROUTES.MY_ACCOUNT/ROUTES.MY_ACCOUNT_SAVED_SEARCHES'
      ),
      text: t('Saved searches'),
      notificationAmount: savedSearchesNotificationsAmount
    },
    {
      url: translateRoute(
        t,
        '/ROUTES.MY_ACCOUNT/ROUTES.MY_ACCOUNT_MY_AUCTIONS'
      ),
      text: t('My auctions'),
      notificationAmount: auctionNotifications.length
    },
    {
      url: translateRoute(
        t,
        '/ROUTES.MY_ACCOUNT/ROUTES.MY_ACCOUNT_MY_SALES/ROUTES.MY_ACCOUNT_MY_SALES_BUSINESS_PROPOSALS'
      ),
      text: t('My sales'),
      notificationAmount: salesAgreementsNotifications.length
    },
    {
      url: translateRoute(t, '/ROUTES.MY_ACCOUNT/ROUTES.MY_ACCOUNT_SETTINGS'),
      text: t('Account settings')
    }
  ];

  if (isPartnershipOnline && isSwedishDomain) {
    items.splice(4, 0, {
      url: translateRoute(
        t,
        '/ROUTES.MY_ACCOUNT/ROUTES.MY_ACCOUNT_PARTNERSHIP_PAGE'
      ),
      text: t('Partnership page')
    });
  }

  if (!session.isAuthenticated && !session.isHydrated) {
    return (
      <SpinnerContainer>
        <Spinner size="large" speed="fast" color="neutral" />
      </SpinnerContainer>
    );
  }

  if (!session.isAuthenticated) {
    return (
      <ComponentPadding>
        <Section spacing={0} backgroundColor="light" maxWidth={22.563}>
          <SignIn
            onPressCreateAccount={handlePressCreateAccount}
            onPressForgotPassword={handlePressForgotPassword}
          />
        </Section>
      </ComponentPadding>
    );
  }

  if (targetedAuctionsFeatureEnabled) {
    session.member.targetedAuctionGroups?.forEach(groupId => {
      const targetedAuctionGroupName = getTargetedAuctionGroupNameById(groupId);
      const targetedAuctionGroupUrl = getTargetedAuctionGroupUrlById(groupId);

      items.push({
        url: translateRoute(
          t,
          `/ROUTES.MY_ACCOUNT/ROUTES.MY_ACCOUNT_TARGETED_AUCTIONS/${targetedAuctionGroupUrl}`
        ),
        text: targetedAuctionGroupName
      });
    });
  }

  return (
    <Content>
      <Head title={t('PAGE_TITLES.MY_ACCOUNT')} />
      <SubMenuContainer>
        <SubMenuNav>
          {items.map(({ url, text, notificationAmount }, key) => {
            return (
              <SubMenuNavItem
                key={key}
                isActive={isActiveUrl(t, url, location.pathname, {
                  exact: false
                })}
              >
                <StyledLink to={url}>
                  {text}
                  {notificationAmount !== 0 && (
                    <StyledBadge notificationAmount={notificationAmount} />
                  )}
                </StyledLink>
              </SubMenuNavItem>
            );
          })}
        </SubMenuNav>
      </SubMenuContainer>
      {/* This renders the children specified in the routes config */}
      {renderRoutes(props?.route?.routes, { session, auctionNotifications })}
    </Content>
  );
};

interface StateProps {
  auctionNotifications: AuctionNotification[];
  favouriteNotifications: FavouriteNotification[];
  savedSearchesNotifications: SavedSearchNotification[];
  salesAgreementsNotifications: SalesAgreementNotification[];
}

const mapStateToProps = createStructuredSelector<ReduxStore, StateProps>({
  auctionNotifications,
  favouriteNotifications,
  savedSearchesNotifications,
  salesAgreementsNotifications
});

export default withRouter(connect(mapStateToProps)(Account));
